/**
 * Ask Salah types (sal-user) — aligned with sal-api unified `subscription` rows where
 * `product === 'ask-salah'`. Plan/session fields are exposed on GET `/ask-salah/subscription`
 * as a flat object (sourced from `metadata` + dates on the server).
 */

/** Matches quotation default and unified subscription rows */
export const SUBSCRIPTION_PRODUCT_SAL125 = 'sal125';
export const SUBSCRIPTION_PRODUCT_ASK_SALAH = 'ask-salah';

/** True when the row is the main SAL125 subscription (legacy rows may omit `product`). */
export function isSal125SubscriptionProduct(product: string | null | undefined): boolean {
  return !product || product === SUBSCRIPTION_PRODUCT_SAL125;
}

// Plan types available
export type AskSalahPlanType = 'trial' | 'basic' | 'premium';

// Billing cycles for paid plans
export type AskSalahBillingCycle = 'monthly' | 'yearly';

/** Subset emphasized in UI; API may also send other unified subscription statuses */
export type AskSalahSubscriptionStatus =
  | 'pending'
  | 'approved'
  | 'active'
  | 'expired'
  | 'cancelled';

// Session types
export type AskSalahSessionType = 'chat' | 'voice';

// Payment methods (same as SAL125)
export type AskSalahPaymentMethod = 'card' | 'bank' | 'purchase';

/**
 * Shape returned by GET `/ask-salah/subscription` (unified subscription + metadata).
 */
export interface AskSalahSubscription {
  id: number;
  /** From `metadata.planType` (lowercase) */
  planType: AskSalahPlanType | string;
  billingCycle?: AskSalahBillingCycle | null;
  startDate: string;
  endDate: string;
  status: AskSalahSubscriptionStatus | string;
  sessionsUsedThisMonth: number;
  sessionsLimit: number;
  lastSessionResetDate?: string;
}

/**
 * Ask Salah Session record
 */
export interface AskSalahSession {
  id: number;
  sessionType: AskSalahSessionType;
  startedAt: string; // ISO date string
  endedAt?: string; // ISO date string
  metadata?: string;
}

/**
 * Plan details for display
 */
export interface AskSalahPlan {
  name: string;
  type: AskSalahPlanType;
  monthlyPrice: number;
  yearlyPrice: number;
  sessionsPerMonth: number;
  features: string[];
  isPopular?: boolean;
}

/**
 * Trial eligibility response
 * 
 * Eligibility Rules:
 * - Ask Salah FREE TRIAL: Available to SAL125 free trial OR paid users
 * - Ask Salah PAID PLANS: Require SAL125 PAID subscription (not free trial)
 */
export interface AskSalahTrialEligibility {
  eligible: boolean;
  reason?: 'no_sal125_subscription' | 'already_used' | 'has_paid_subscription';
}

/**
 * Paid plan eligibility check
 * For Basic/Premium plans
 */
export interface AskSalahPaidPlanEligibility {
  eligible: boolean;
  reason?: 'no_sal125_subscription' | 'sal125_free_trial_only' | 'has_active_subscription';
}

/**
 * Session usage from GET `/ask-salah/sessions/usage` (metadata-backed counters server-side).
 */
export interface AskSalahSessionUsage {
  used: number;
  limit: number;
  remaining: number;
  /** ISO 8601 string from API */
  resetDate: string;
}

/**
 * Token generation response
 */
export interface AskSalahTokenResponse {
  token: string;
}

/**
 * Subscription creation request (DEPRECATED - use CreateAskSalahOrderRequest)
 */
export interface CreateAskSalahSubscriptionRequest {
  planType: 'basic' | 'premium';
  billingCycle: AskSalahBillingCycle;
}

/**
 * Ask Salah order creation request (mirrors SAL125 CreateOrderDTO)
 */
export interface CreateAskSalahOrderRequest {
  quotationId?: number;
  planType: AskSalahPlanType;
  billingCycle?: AskSalahBillingCycle;
  paymentMethod: AskSalahPaymentMethod;
  proveUrl?: string;
  gateway?: 'tap' | 'myfatoorah';
}

/**
 * Ask Salah order response (mirrors SAL125 order response)
 */
export interface AskSalahOrderResponse {
  id: number;
  url?: string; // Payment URL for card payments
  message?: string; // Status message for manual payments
}

/**
 * Ask Salah order status types
 */
export type AskSalahOrderStatus = 'pending' | 'approved' | 'declined' | 'cancelled';

/**
 * Constants for Ask Salah plans
 * Features use translation keys (askSalah.feature.*)
 */
export const ASK_SALAH_PLANS: Record<AskSalahPlanType, Omit<AskSalahPlan, 'type'>> = {
  trial: {
    name: 'askSalah.trial.name',
    monthlyPrice: 0,
    yearlyPrice: 0,
    sessionsPerMonth: 3,
    features: ['askSalah.feature.trial.sessions', 'askSalah.feature.textChat'],
  },
  basic: {
    name: 'askSalah.basic.name',
    monthlyPrice: 11.99,
    yearlyPrice: 120.0,
    sessionsPerMonth: 10,
    features: ['askSalah.feature.basic.sessions', 'askSalah.feature.textChat'],
  },
  premium: {
    name: 'askSalah.premium.name',
    monthlyPrice: 24.99,
    yearlyPrice: 250.0,
    sessionsPerMonth: 10,
    features: [
      'askSalah.feature.premium.sessions',
      'askSalah.feature.voiceInteraction',
      'askSalah.feature.salahVoice',
    ],
    isPopular: true,
  },
};

